import {
  type CallhomeCamera,
  type CallhomeSite,
  type DeviceEntity,
  ScheduleState,
} from '@solinkcorp/api-types';
import { type UserSession } from '@solinkcorp/authentication';
import { type AlarmContactDto } from '@solinkcorp/callhome';
import { type LocaleDefinition } from '@solinkcorp/locale';
import settings from '@solinkcorp/settings';
import {
  isInSchedule,
  isSchedule247,
  type Period,
  type ResolvedPeriod,
  type WeeklySchedule,
} from '@solinkcorp/solink-schedule';
import { getAccessibleAddress, getTimezoneAbbr } from '@solinkcorp/utils';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import CameraActions from 'actions/CameraActions';
import * as LocationActions from 'actions/LocationActions';
import { CallhomeApi } from 'clients';
import {
  AlarmConfigState,
  type AlarmContactEntity,
  type AlarmMode,
  type AlarmProviderType,
  type CallhomeAlarmConfig,
  type DisarmMethod,
} from 'clients/CallhomeApiTypes';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import without from 'lodash/without';
import AuthService from 'models/AuthService';
import moment from 'moment-timezone';
import Papa from 'papaparse';
import { timeFormatNumeral } from 'src/core/utils/timeFormat';
import TimeFormatter from 'utils/TimeFormatter';
import {
  ALARM_CONTACT_TYPES,
  ALARM_PROVIDER_TYPES,
  ALARM_SCHEDULE_LEVELS,
  DEVICE_ALARM_MODES,
  EMPTY_WEEK_SCHEDULE,
  END_OF_DAY,
  EVENT_CARD_FIXED_WIDTH,
  GLOBAL_CAMERA_TAGS,
  SELF_MONITORED_PLUS,
  START_OF_DAY,
} from 'vd-constants';
import { getContactsForSite, getPinTypeLabel } from './AlarmContactUtils';
import { filter } from './array';
import * as CameraUtil from './CameraUtil';
import { type VideoDiscoverySite } from './types';

export const NO_SCHEDULE = 'No Schedule';
const ALARM_STATE_10_SEC_REFRESH_INTERVAL = 10 * 1000; // 10 seconds

/**
 * Special value for snoozeFor to indicate camera should be snoozed until next alarm schedule period
 */
export const SNOOZE_UNTIL_NEXT_SCHEDULE = -1;

export const FULL_WEEK_SCHEDULE = {
  0: [{ start: START_OF_DAY, end: END_OF_DAY }],
  1: [{ start: START_OF_DAY, end: END_OF_DAY }],
  2: [{ start: START_OF_DAY, end: END_OF_DAY }],
  3: [{ start: START_OF_DAY, end: END_OF_DAY }],
  4: [{ start: START_OF_DAY, end: END_OF_DAY }],
  5: [{ start: START_OF_DAY, end: END_OF_DAY }],
  6: [{ start: START_OF_DAY, end: END_OF_DAY }],
};

export const getCameraAlarmScheduleInfo = (
  location: CallhomeSite,
  camera: CallhomeCamera,
  device: DeviceEntity,
) => {
  const isMotionAlarmEnabled = camera.isMotionAlarmEnabled || false;
  const cameraMotionAlarmSchedule = camera.motionAlarmSchedule;
  const locationMotionAlarmSchedule = location.motionAlarmSchedule;
  const locationOperatingHoursSchedule = device.operatingHoursSchedule;

  let isScheduleBasedOnOperatingHours = false;

  if (
    !cameraMotionAlarmSchedule &&
    !locationMotionAlarmSchedule &&
    !!locationOperatingHoursSchedule
  ) {
    isScheduleBasedOnOperatingHours = true;
  }

  // Assign `schedule`
  const schedule =
    cameraMotionAlarmSchedule || locationMotionAlarmSchedule || locationOperatingHoursSchedule;

  // Assign `scheduleLevel`
  let scheduleLevel;
  if (cameraMotionAlarmSchedule) {
    scheduleLevel = ALARM_SCHEDULE_LEVELS.CAMERA;
  } else if (!!locationMotionAlarmSchedule || !!locationOperatingHoursSchedule) {
    scheduleLevel = ALARM_SCHEDULE_LEVELS.LOCATION;
  } else {
    scheduleLevel = ALARM_SCHEDULE_LEVELS.NONE;
  }

  const weeklySchedule = schedule?.schedule;
  const cameraWeeklySchedule = cameraMotionAlarmSchedule?.schedule;
  const effectiveScheduleState = schedule?.state;

  // TODO: need to re-evaluate these conditions for alarm v2
  let toggleButtonScheduleState;
  if (isMotionAlarmEnabled === false) {
    // Motion Alarm not enabled for this camera or schedule state set OFF
    toggleButtonScheduleState = ScheduleState.OFF;
  } else if (
    isMotionAlarmEnabled &&
    cameraMotionAlarmSchedule &&
    (effectiveScheduleState === ScheduleState.ALWAYS_ON ||
      (!!cameraWeeklySchedule && isSchedule247(cameraWeeklySchedule)))
  ) {
    // Motion Alarm state is ON or schedule is a 24/7
    toggleButtonScheduleState = ScheduleState.ALWAYS_ON;
  } else {
    // Motion Alarm following the schedule
    toggleButtonScheduleState = ScheduleState.SCHEDULE;
  }

  let inSchedule = false;
  if (schedule) {
    // Schedule based on Operating hours...
    if (isScheduleBasedOnOperatingHours) {
      if (!weeklySchedule) {
        inSchedule = false;
      } else if (isSchedule247(weeklySchedule)) {
        // If the operating hours is 24/7 then the inverse is empty and thus alarms
        // can never be in schedule.
        inSchedule = false;
      } else {
        // Otherwise check if now is outside of the device operating hours
        inSchedule = isOutsideDeviceOperatingHours(weeklySchedule, location);
      }
    }
    // Location or Camera Schedule
    else {
      if (effectiveScheduleState === ScheduleState.OFF) {
        inSchedule = false;
      } else if (effectiveScheduleState === ScheduleState.ALWAYS_ON) {
        inSchedule = true;
      } else if (effectiveScheduleState === ScheduleState.SCHEDULE && !!weeklySchedule) {
        inSchedule = isLocationInSchedule(weeklySchedule, location);
      }
    }
  }

  const scheduleMuteUntil = get(locationMotionAlarmSchedule, 'muteUntil');
  const isSnoozed = scheduleMuteUntil !== 0;
  // TODO Improvement actually look at `muteUtil` if its a unix date and do fancy stuff
  // to display "Snoozed until XXX"

  // If the schedule type is OFF or ALWAYS_ON, then no change is possible.
  const msToScheduleChange =
    effectiveScheduleState === ScheduleState.SCHEDULE ? getMsToScheduleChange() : null;

  return {
    inSchedule,
    isSnoozed,
    msToScheduleChange,
    schedule,
    scheduleLevel,
    toggleButtonScheduleState,
  };
};

/**
 * @returns {boolean} Whether now is outside the operating hours according to the device timezone
 */
const isOutsideDeviceOperatingHours = (weeklySchedule: WeeklySchedule, location: CallhomeSite) =>
  !!location.timezone && !isInSchedule(weeklySchedule, Date.now(), location.timezone);

/**
 * @returns {boolean} Whether now is in a motion alarm schedule according to the device timezone
 */
export const isLocationInSchedule = (weeklySchedule: WeeklySchedule, location: CallhomeSite) =>
  !!location.timezone && !isInSchedule(weeklySchedule, Date.now(), location.timezone);

/**
 * @param locale - the locale.
 * @param day - the day to get the day-of-week string for.
 * @param currentTime - the current time, used to return "today" if the day is today.
 * @param timezone - the timezone to use (e.g. "America/Montreal").
 */
export function getDayOfWeekString(
  day: Date | number,
  currentTime: Date | number,
  timezone: string,
  locale: LocaleDefinition,
): string {
  const dayMoment = moment.tz(day, timezone);
  const isCurrentDay = moment.tz(currentTime, timezone).isSame(dayMoment, 'day');
  if (isCurrentDay) {
    return locale.date.today;
  }

  const dayOfWeek = dayMoment.day();
  const { sunday, monday, tuesday, wednesday, thursday, friday, saturday } =
    locale.notificationsPage;
  const daysInWeek = [sunday, monday, tuesday, wednesday, thursday, friday, saturday];
  return daysInWeek[dayOfWeek];
}

/**
 * Warning: This function is not working/tested yet so it is just always returning 10000 for now.
 *
 * @returns {number} milliseconds until being in/out of schedule is going to change
 */
const getMsToScheduleChange = () => {
  // TODO Improvement, stop using 10s interval and test/fix this function.
  return ALARM_STATE_10_SEC_REFRESH_INTERVAL;
};

export const getLocationAlarmSchedule = (location: VideoDiscoverySite) => {
  const schedule = location.motionAlarmSchedule?.schedule;
  return isEmpty(schedule) ? EMPTY_WEEK_SCHEDULE : schedule;
};

export const getActualLocationAlarmSchedule = (
  location: VideoDiscoverySite | undefined,
  isMerged: boolean = false,
): WeeklySchedule | undefined => {
  const { schedule, mergedSchedule, state } = location?.motionAlarmSchedule || {};
  const scheduleToUse = isMerged ? mergedSchedule : schedule;
  const emptySchedule =
    !scheduleToUse || typeof scheduleToUse !== 'object' || Object.keys(scheduleToUse).length === 0;

  if (state === ScheduleState.ALWAYS_ON) {
    return FULL_WEEK_SCHEDULE;
  } else if (emptySchedule || state === ScheduleState.OFF) {
    return EMPTY_WEEK_SCHEDULE;
  }
  return scheduleToUse;
};

export const getLocationAlarmScheduleState = (location: VideoDiscoverySite) =>
  location.motionAlarmSchedule?.state || ScheduleState.OFF;

export const getNormalizedSchedule = (schedule: WeeklySchedule | undefined | null) => {
  if (!schedule) {
    return EMPTY_WEEK_SCHEDULE;
  }

  const normalizedSchedule = cloneDeep(schedule);

  for (const key of [0, 1, 2, 3, 4, 5, 6] as const) {
    const day = normalizedSchedule[key];
    if (!day || day.length === 0) {
      delete normalizedSchedule[key];
    } else {
      normalizedSchedule[key] = day.filter(
        (period) => get(period, 'start') !== null && get(period, 'end') !== null,
      );
    }
  }

  // "Normalized schedule" in terms of `@solinkcorp/solink-schedule`
  return normalizedSchedule;
};

/** Only cameras with alarm enabled and motion zone setup are alarm cameras */
export const sortCamerasByAlarm = async (
  cameras: CallhomeCamera[],
  localDevices: Record<string, boolean | undefined>,
  devicesById: Record<string, DeviceEntity>,
  isStandardUser: boolean,
) => {
  const alarmCameras: CallhomeCamera[] = [];
  const nonAlarmCameras: CallhomeCamera[] = [];

  const cameraPromises = await Promise.allSettled(
    cameras.map(async (camera) => {
      const device = devicesById[camera.deviceId];
      const isLocal = localDevices[device?.id || ''];
      const accessibleAddress = getAccessibleAddress(device, isLocal) ?? undefined;

      const authDevice = {
        accessibleAddress,
        id: device.id,
        name: device.name,
        currentSoftwareVersion: device.currentSoftwareVersion,
      };

      let isAlarmCamera = false;
      if (camera.isMotionAlarmEnabled) {
        if (camera.cameraTagIds.includes(GLOBAL_CAMERA_TAGS.LOITERING)) {
          isAlarmCamera = true;
        } else {
          try {
            isAlarmCamera = await CameraUtil.hasMotionZones(
              authDevice,
              camera,
              AuthService.getLocalFetchParams(authDevice, `/cameras/${camera.cameraId}/json`),
              isStandardUser,
            );
          } catch (err) {
            console.warn(`Could not check motion zones for camera ${camera.id}`, err);
          }
        }
      }

      return { camera, isAlarmCamera };
    }),
  );

  cameraPromises.forEach((result) => {
    if (result.status === 'fulfilled') {
      const { camera, isAlarmCamera } = result.value;
      (isAlarmCamera ? alarmCameras : nonAlarmCameras).push(camera);
    } else {
      console.error('Failed to process camera:', result.reason);
    }
  });

  return {
    alarmCameraIds: alarmCameras.map((c) => c.id),
    nonAlarmCameraIds: nonAlarmCameras.map((c) => c.id),
    alarmCameras,
    nonAlarmCameras,
  };
};

export const isLocationAlarmConfigured = (
  location: VideoDiscoverySite,
  alarmCameras: CallhomeCamera[] | undefined,
  customerAlarmContacts?: AlarmContactDto[],
  // Phase 3 cleanup (SL-12707): customer-level migration flag, supplied by the caller
  // (read via `useIsAlarmContactMigrated`). Remove this param and the isMigrated check
  // once all customers are migrated.
  isAlarmContactMigrated = false,
) => {
  const { alarmContacts, alarmConfig } = location;

  if (alarmConfig?.mode === DEVICE_ALARM_MODES.DISABLED) {
    return false;
  }
  const isMigrated = isAlarmContactMigrated;

  const hasAlarmContact =
    isMigrated && customerAlarmContacts
      ? getContactsForSite(customerAlarmContacts, location.id).siteAlarmContacts.length > 0
      : (alarmContacts?.filter((contact) => contact.type !== ALARM_CONTACT_TYPES.DTMFPIN).length ??
          0) > 0;

  if (!hasAlarmContact) {
    return false;
  }

  const hasAlarmCameras = alarmCameras && alarmCameras?.length >= 1;
  return hasAlarmCameras;
};

export const atLeastOneCameraHaveMotionZones = async (
  authLocation: AuthLocation,
  cameras: CallhomeCamera[],
  isStandardUser: boolean,
) => {
  let haveMotionZones = false;
  for (let i = 0; i < cameras.length; i++) {
    try {
      const fetchParams = AuthService.getLocalFetchParams(
        authLocation,
        `/cameras/${cameras[i].cameraId}/json`,
      );
      const hasMotionZones = await CameraUtil.hasMotionZones(
        authLocation,
        cameras[i],
        fetchParams,
        isStandardUser,
      );

      if (hasMotionZones) {
        haveMotionZones = true;
        break;
      }
    } catch (err) {
      console.error(`Could not fetch camera's motion zones for ${cameras[i].id}`, err);
    }
  }

  return haveMotionZones;
};

export const getRecurringActivities = async (locationId: string) => {
  return CallhomeApi().listRecurringActivities(locationId);
};

export const isAlarmUntilUnixValid = (
  untilUnix: number,
  currentTime: number,
  timezoneId: string,
) => {
  const currentDeviceTime = moment(currentTime).tz(timezoneId).valueOf();
  const weekAfterCurrentDeviceTime = moment(currentDeviceTime).add(7, 'day').valueOf();
  const untilTime = untilUnix * 1000;
  return untilTime > currentDeviceTime && untilTime < weekAfterCurrentDeviceTime;
};

/**
 * Check if a location's motion alarm schedule or motion alarm state updated
 * The motion alarm state here is for alarm V1?
 */
export const isLocationAlarmScheduleUpdated = ({
  location,
  newMotionAlarmSchedule,
  newMotionAlarmState,
}: {
  location: VideoDiscoverySite;
  newMotionAlarmSchedule: WeeklySchedule | undefined;
  newMotionAlarmState?: string;
}) => {
  const { motionAlarmSchedule, motionAlarmScheduleId } = location;
  const normalizedNewMotionAlarmSchedule = getNormalizedSchedule(newMotionAlarmSchedule);

  if (motionAlarmScheduleId) {
    return (
      (newMotionAlarmSchedule &&
        !isEqual(motionAlarmSchedule?.schedule, normalizedNewMotionAlarmSchedule)) ||
      (newMotionAlarmState && motionAlarmSchedule?.state !== newMotionAlarmState)
    );
  }
  // in V1 the motionAlarmScheduleId would never be null, technically we don't need this
  // but it should be useful for V2, so keep the code for now
  return (
    !isEmpty(normalizedNewMotionAlarmSchedule) ||
    (isEmpty(normalizedNewMotionAlarmSchedule) && newMotionAlarmState === ScheduleState.ALWAYS_ON)
  );
};

export const updateLocationAlarmSchedule = (
  location: VideoDiscoverySite,
  motionAlarmSchedule: WeeklySchedule | undefined,
) => {
  if (!location) {
    throw new Error(`Can't update alarm with no selected location.`);
  }

  const { id: locationId, motionAlarmSchedule: originalMotionAlarmSchedule } = location;

  const newMotionAlarmSchedule = getNormalizedSchedule(motionAlarmSchedule);
  const motionAlarmScheduleMuteUntil = originalMotionAlarmSchedule?.muteUntil;
  const scheduleState = ScheduleState.SCHEDULE;

  return LocationActions.setLocationMotionAlarmSchedule({
    cameraId: undefined,
    locationId,
    muteUntil: motionAlarmScheduleMuteUntil || 0,
    schedule: newMotionAlarmSchedule,
    state: scheduleState,
  });
};

export async function setCameraAlarmScheduleAlwaysOn(camera: CallhomeCamera): Promise<void> {
  const { id } = camera;

  if (!id) {
    return;
  }

  return CameraActions.setCameraAlarmSchedule({
    id,
    state: ScheduleState.ALWAYS_ON,
  });
}

export const setCameraAlarmFollowSchedule = async ({
  camera,
  useProxy,
  isLocal,
}: {
  camera: CallhomeCamera;
  isLocal: boolean;
  useProxy?: boolean;
}) => {
  const { id: cameraId } = camera;

  if (!cameraId) {
    return;
  }

  const accessibleAddress = getAccessibleAddress(camera, isLocal);

  return CameraActions.enableMotionAlarmsOnCamera({
    deviceUrl: accessibleAddress,
    id: cameraId,
    deviceId: camera.deviceId,
    useProxy,
  });
};

export const getAlarmGuests = (location: VideoDiscoverySite) =>
  location?.alarmContacts?.filter((contact) => contact.type === ALARM_CONTACT_TYPES.GUEST);

export const getAlarmContacts = (location: VideoDiscoverySite) =>
  location?.alarmContacts
    ?.filter(
      (contact) =>
        contact.type !== ALARM_CONTACT_TYPES.GUEST && contact.type !== ALARM_CONTACT_TYPES.DTMFPIN,
    )
    .sort((a, b) => {
      if (!a.priority && !b.priority) {
        return 0; // Both priorities are falsy, they are considered equal
      } else if (!a.priority) {
        return 1; // a has no priority, b comes first
      } else if (!b.priority) {
        return -1; // b has no priority, a comes first
      }
      return a.priority - b.priority; // Both have priorities, sort normally
    }) ?? [];

export const isAlarmGuestAccessEnabled = (alarmConfig: CallhomeAlarmConfig | undefined) =>
  alarmConfig?.providerSettings?.guestAccessEnabled ?? true;

export const getArmUntilTimeString = ({
  locale,
  locationTimeZone,
  armUntil,
}: {
  locale: LocaleDefinition;
  locationTimeZone: string;
  armUntil: number;
}) => {
  const dayString = getDayOfWeekString(armUntil, Date.now(), locationTimeZone, locale);
  const formattedArmUntil = TimeFormatter.getTimeString({
    time: armUntil,
    timeZoneId: locationTimeZone,
  });

  const timeZoneName = getTimezoneAbbr(locationTimeZone);
  return `${formattedArmUntil} ${dayString} ${timeZoneName}`;
};

export const getDisarmUntilTimeString = ({
  locale,
  nextAlarmedPeriod,
  locationTimeZone,
  disarmUntil,
  disarmUntilFuture,
}: {
  locale: LocaleDefinition;
  nextAlarmedPeriod?: Readonly<ResolvedPeriod>;
  locationTimeZone: string;
  disarmUntil: number;
  disarmUntilFuture: boolean;
}) => {
  const formattedDisarmUntil = disarmUntilFuture
    ? TimeFormatter.getTimeString({ time: disarmUntil, timeZoneId: locationTimeZone })
    : '';

  let nextPeriodString = '';
  if (
    nextAlarmedPeriod &&
    moment
      .tz(nextAlarmedPeriod.start, locationTimeZone)
      .isSame(moment.tz(disarmUntil, locationTimeZone), 'day')
  ) {
    nextPeriodString =
      ' ' + getDayOfWeekString(nextAlarmedPeriod.start, Date.now(), locationTimeZone, locale);
  }

  const timeZoneName = getTimezoneAbbr(locationTimeZone);
  return `${formattedDisarmUntil}${nextPeriodString} ${timeZoneName}`;
};

export const getDisarmUntilPeriodTimeString = ({
  disarmUntilStart,
  disarmUntilEnd,
  locationTimeZoneId,
  locale,
}: {
  disarmUntilStart: number;
  disarmUntilEnd: number;
  locationTimeZoneId: string;
  locale: LocaleDefinition;
}) => {
  const dayOfWeek = getDayOfWeekString(disarmUntilStart, Date.now(), locationTimeZoneId, locale);
  const startTime = timeFormatNumeral(disarmUntilStart, {
    timeZone: locationTimeZoneId,
    hideTimeZone: true,
  });
  const endTime = timeFormatNumeral(disarmUntilEnd, {
    timeZone: locationTimeZoneId,
    hideTimeZone: false,
  });
  return `${dayOfWeek} ${startTime} - ${endTime}`;
};

type ExportAlarmContact = Pick<AlarmContactEntity, 'name' | 'email' | 'phoneNumber'>;

const formatAlarmContacts = (alarmContacts: ExportAlarmContact[]) => {
  let contacts = '';
  alarmContacts.forEach(({ name, email, phoneNumber }, index) => {
    if (index !== 0) {
      contacts += ' | ';
    }

    if (email && phoneNumber) {
      contacts += `${name} (${email}) / ${phoneNumber}`;
    } else if (email) {
      contacts += `${name} (${email})`;
    } else if (phoneNumber) {
      contacts += `${name} (${phoneNumber})`;
    }
  });
  return contacts;
};

type DayOfWeekString = '0' | '1' | '2' | '3' | '4' | '5' | '6';

export const getDayOfWeek = (
  day: DayOfWeekString,
  { notificationsPage: locale }: LocaleDefinition,
) => {
  const dayOfWeekMap = {
    0: locale.sunday,
    1: locale.monday,
    2: locale.tuesday,
    3: locale.wednesday,
    4: locale.thursday,
    5: locale.friday,
    6: locale.saturday,
  };
  return dayOfWeekMap[day];
};

const formatAlarmSchedule = (
  weekSchedule: WeeklySchedule | undefined,
  locale: LocaleDefinition,
) => {
  let schedule = '';

  for (const day of ['0', '1', '2', '3', '4', '5', '6'] as const) {
    if (schedule) {
      schedule += ' | ';
    }

    const daySchedule = weekSchedule?.[day];
    const dayOfWeek = getDayOfWeek(day, locale);

    if (daySchedule && daySchedule.length > 0) {
      schedule += `${dayOfWeek} `;
    }

    daySchedule?.forEach((timeSpan) => {
      const start = moment(timeSpan.start, 'HH mm').format('hh:mm A');
      const end = moment(timeSpan.end, 'HH mm').format('hh:mm A');
      schedule += `(${start} - ${end}) `;
    });
  }
  return schedule;
};

const getArmedCameras = (cameras: CallhomeCamera[]) => {
  let armedCameras = '';
  cameras.forEach(({ name, isMotionAlarmEnabled }) => {
    if (isMotionAlarmEnabled) {
      if (armedCameras) {
        armedCameras += ' | ';
      }
      armedCameras += name;
    }
  });
  return armedCameras;
};

const formatAlarmDescription = (mode: AlarmMode, state: AlarmConfigState, schedule: string) => {
  let description = '';

  if (mode === DEVICE_ALARM_MODES.TEST) {
    description += '[TEST] ';
  }

  if (mode === DEVICE_ALARM_MODES.DISABLED) {
    description += 'Activate alarm system';
  } else if (state === AlarmConfigState.DISARMED_FOREVER || !schedule) {
    description += 'Always off';
  } else if (state === AlarmConfigState.ARMED_FOREVER) {
    description += 'Always on';
  } else if (state === AlarmConfigState.SCHEDULE) {
    description += 'On schedule';
  }
  return description;
};

export const hasSelfMonitoredPlus = (alarmConfig: CallhomeAlarmConfig) =>
  alarmConfig.addonFeatures?.includes(SELF_MONITORED_PLUS) ?? false;

export const isProMonitored = (providerType: AlarmProviderType | undefined, isVerifEye3: boolean) =>
  providerType === ALARM_PROVIDER_TYPES.NOONLIGHT ||
  (providerType === ALARM_PROVIDER_TYPES.MONITOR_CENTER && isVerifEye3);

export const getAlarmProviderLabel = (apt: AlarmProviderType, isVerifEye3: boolean) => {
  switch (apt) {
    case ALARM_PROVIDER_TYPES.SOLINK:
      return 'Self-Monitored';
    case ALARM_PROVIDER_TYPES.NOONLIGHT:
      return 'Monitored';
    case ALARM_PROVIDER_TYPES.MONITOR_CENTER:
      return isVerifEye3 ? 'Monitored' : 'VerifEye';
    default:
      return '?';
  }
};

export const getDisarmMethodLabel = (dm: DisarmMethod | null) => {
  switch (dm) {
    case 'PINPAD':
      return 'Video Alarm Tablet';
    case 'DTMF':
      return 'Location Phone';
    default:
      return 'None';
  }
};

export const organizeAlarmConfigDataForExport = (
  locations: VideoDiscoverySite[],
  locale: LocaleDefinition,
  isVerifEye3: boolean,
  customerAlarmContacts?: AlarmContactDto[],
  isCustomerAlarmContactMigrated = false,
) => {
  const data = [locale.exportPage.configurationTableHeaders];
  locations.forEach(
    ({ alarmConfig, alarmContacts, cameras, id, motionAlarmSchedule, name, address }) => {
      const { mode, providerType, state, disarmMethod, locationNote, note } = alarmConfig;
      const callhomeUrl = getManageURL(id);
      const product = getAlarmProviderLabel(providerType, isVerifEye3);
      const plus = hasSelfMonitoredPlus(alarmConfig);
      const disarm = getDisarmMethodLabel(disarmMethod);
      const weekSchedule = motionAlarmSchedule?.schedule;
      // Phase 3 cleanup (SL-12707): once all customers are migrated, always use
      // getContactsForSite(...).sitePinContacts and remove the legacy site alarmContacts path.
      const contactsForExport = isCustomerAlarmContactMigrated
        ? getContactsForSite(customerAlarmContacts, id).sitePinContacts
        : filter(alarmContacts).excludeBy('type', [ALARM_CONTACT_TYPES.DTMFPIN]).value();
      const contacts = formatAlarmContacts(contactsForExport);
      const schedule = formatAlarmSchedule(weekSchedule, locale);
      const armedCameras = getArmedCameras(cameras);
      const description = formatAlarmDescription(mode, state, schedule);
      const row = [
        name,
        address,
        callhomeUrl,
        mode,
        product,
        plus,
        description,
        disarm,
        contacts,
        locationNote,
        note,
        schedule,
        armedCameras,
      ].map((item) => toCSVData(item, locale));
      data.push(row);
    },
  );
  return toCSV(data);
};

export const getManageURL = (id: string) => `${settings.manage_url}/device/${id}/alarm/overview`;

export const toCSVData = (
  value: string | boolean | null | undefined,
  locale: LocaleDefinition,
): string => {
  if (typeof value === 'boolean') {
    return value ? locale.general.yes : locale.general.no;
  }
  return String(value ?? '');
};

export const toCSV = (data: string[][]) => {
  return Papa.unparse(data, {
    quotes: true,
    escapeChar: '"',
    delimiter: ',',
    newline: '\n',
  });
};

const pluralize = (value: number, unit: string) =>
  value === 1 ? `1 ${unit}` : `${Number.isInteger(value) ? value : value.toFixed(2)} ${unit}s`;

export const formatDisarmTime = (
  disarmForSeconds: number | undefined,
  locale: LocaleDefinition,
) => {
  if (disarmForSeconds === undefined) return '';
  if (disarmForSeconds === -1) return locale.alarms.dtmfPinUserDisarmUntilNextPeriod;
  if (disarmForSeconds >= 3600) {
    return pluralize(disarmForSeconds / 3600, locale.general.hourAbbreviation);
  }
  if (disarmForSeconds >= 60) {
    return pluralize(disarmForSeconds / 60, locale.general.minuteAbbreviation);
  }
  return pluralize(disarmForSeconds, locale.general.secondAbbreviation);
};

export const organizePINsForExport = (
  locations: VideoDiscoverySite[],
  locale: LocaleDefinition,
  customerAlarmContacts?: AlarmContactDto[],
  isCustomerAlarmContactMigrated = false,
) => {
  const data = [locale.exportPage.PINsTableHeaders];
  locations.forEach(({ name: locationName, id, alarmContacts, timezone }) => {
    const url = getManageURL(id);
    // Phase 3 cleanup (SL-12707): remove legacy DTMFPIN site alarmContacts path once all customers are migrated.
    const pinRows = isCustomerAlarmContactMigrated
      ? getContactsForSite(customerAlarmContacts, id).sitePinContacts.map((contact) => [
          contact.name,
          getPinTypeLabel(contact.pinType, locale),
          contact.startsOnDate ?? locale.alarms.pinStartsImmediately,
          contact.expiresOnDate ?? locale.alarms.pinDoesNotExpire,
          formatDisarmTime(contact.disarmForSeconds, locale),
        ])
      : filter(alarmContacts)
          .includeBy('type', [ALARM_CONTACT_TYPES.DTMFPIN])
          .value()
          .map((contact) => [
            contact.name,
            getPinTypeLabel(null, locale),
            locale.alarms.pinStartsImmediately,
            contact.expiresAt == null || contact.expiresAt < 0
              ? locale.alarms.pinDoesNotExpire
              : TimeFormatter.getDateTime(
                  { timezoneId: timezone ?? undefined, seconds: false },
                  new Date(contact.expiresAt * 1000),
                ),
            formatDisarmTime(contact.disarmForSeconds, locale),
          ]);
    pinRows.forEach((pinRow) => {
      data.push([locationName, url, ...pinRow].map((item) => toCSVData(item, locale)));
    });
  });
  return toCSV(data);
};

/*
 * This takes a raw alarm schedule in the following format {'0': [{ end: '05:00', start: '00:00' }]}
 * It then formats it to be rendered in the event cards for the mobile apps and VD
 * \n pushes it to a new line, and each empty space indents the text
 * For.e.g \n Sunday                      00:00 - 05:00
 */
export const formatAlarmScheduleAuditEvent = (
  schedule: WeeklySchedule | undefined,
  locale: LocaleDefinition,
) => {
  let formattedAlarmSchedule = '';
  if (!schedule || isAlarmScheduleEmpty(schedule)) {
    return NO_SCHEDULE;
  }
  const dateCharacters = 13; // e.g. '00:00 - 00:00'

  Object.keys(schedule).forEach((day, dayScheduleIndex) => {
    const dayOfWeek = getDayOfWeek(day as DayOfWeekString, locale);
    const daySchedule = (schedule as { [key: string]: Period[] })[day];
    if (daySchedule.length) {
      // Move to next line, if it's the start of the schedule
      if (!formattedAlarmSchedule) formattedAlarmSchedule += '\n';
      // Insert the day of week
      formattedAlarmSchedule += dayOfWeek;
      daySchedule.forEach((timePeriod: { start: string; end: string }, timePeriodIndex: number) => {
        // Calculate the empty spaces based on the available space
        const emptySpacing = ' '.repeat(
          timePeriodIndex === 0
            ? EVENT_CARD_FIXED_WIDTH - dayOfWeek.length - dateCharacters
            : EVENT_CARD_FIXED_WIDTH - dateCharacters,
        );
        formattedAlarmSchedule += emptySpacing;
        // Add in the time period
        formattedAlarmSchedule += `${timePeriod['start']} - ${timePeriod['end']}`;
        // Only add a new line if it's NOT the last time period in the list
        // There are some cases where we have an empty time period (must check for this)
        if (
          daySchedule.length - 1 !== timePeriodIndex ||
          (Object.keys(schedule).length - 1 !== dayScheduleIndex &&
            (schedule as { [key: string]: Period[] })[Object.keys(schedule)[dayScheduleIndex + 1]]
              .length)
        ) {
          formattedAlarmSchedule += '\n';
        }
      });
    }
  });

  return formattedAlarmSchedule;
};

function isAlarmScheduleEmpty(schedule: WeeklySchedule | undefined): boolean {
  if (!schedule) return true;

  for (const key in schedule) {
    if ((schedule as { [key: string]: Period[] })[key].length) {
      return false;
    }
  }
  return true;
}

export function getAlarmProviderType(alarmConfig: CallhomeAlarmConfig) {
  return alarmConfig.providerType || ALARM_PROVIDER_TYPES.SOLINK;
}

export type AlarmMonitoringMode =
  | typeof GLOBAL_CAMERA_TAGS.INTRUSION_DETECTION
  | typeof GLOBAL_CAMERA_TAGS.REMOTE_GUARDING
  | typeof GLOBAL_CAMERA_TAGS.STAY;

const ALARM_ZONE_MONITORING_MODES: Record<string, AlarmMonitoringMode> = {
  ALARM_ZONE_1: GLOBAL_CAMERA_TAGS.INTRUSION_DETECTION,
  ALARM_ZONE_2: GLOBAL_CAMERA_TAGS.REMOTE_GUARDING,
  ALARM_ZONE_3: GLOBAL_CAMERA_TAGS.STAY,
};

// Sentinel MC reports on `meta.alarmMonitoringMode` for alarms whose site configuration has no
// monitoring mode. Doubles as a filter value (matching it keeps mode-less alarms in the list when
// a mode is selected) and as the signal that there is no mode worth showing to the user.
export const NO_MONITORING_MODE = 'NO_MONITORING_MODE';

// alarmMonitoringMode must be the provider's canonical mode string (e.g. INTRUSION_DETECTION),
// not the raw zone id used for the zone-configuration UI/tags.
export const getAlarmMonitoringMode = (zoneId: string): AlarmMonitoringMode =>
  ALARM_ZONE_MONITORING_MODES[zoneId] ?? zoneId;

const EXPIRING_MONITORING_MODES: string[] = [
  GLOBAL_CAMERA_TAGS.REMOTE_GUARDING,
  GLOBAL_CAMERA_TAGS.STAY,
];

// RG/STAY past modeValidUntil (unix seconds, 0 = never) is treated as INTRUSION_DETECTION.
export const getEffectiveAlarmMonitoringMode = (
  operationSettings: CallhomeAlarmConfig['operationSettings'],
  nowMs: number = Date.now(),
): AlarmMonitoringMode | undefined => {
  const { alarmMonitoringMode, modeValidUntil = 0 } = operationSettings ?? {};
  if (
    alarmMonitoringMode &&
    EXPIRING_MONITORING_MODES.includes(alarmMonitoringMode) &&
    modeValidUntil > 0 &&
    modeValidUntil * 1000 <= nowMs
  ) {
    return GLOBAL_CAMERA_TAGS.INTRUSION_DETECTION;
  }
  return alarmMonitoringMode as AlarmMonitoringMode | undefined;
};

// A zone is considered "active" for the given selected monitoring mode when its canonical mode
// matches, or when no mode is selected/configured (preserves unfiltered behavior for sites that
// don't use VerifEye monitoring modes).
export const isZoneActiveForMode = (
  zoneId: string,
  selectedMonitoringMode?: AlarmMonitoringMode,
): boolean => !selectedMonitoringMode || getAlarmMonitoringMode(zoneId) === selectedMonitoringMode;

// A camera only counts as armed for the selected VerifEye mode when the site itself is armed
// and, if a mode is selected, the camera is tagged into a zone matching that mode. Mirrors the
// armed/orange-outline condition used for the camera thumbnail so callers (e.g. sorting the
// camera grid) stay consistent with what's shown on screen.
//
// Deliberately does NOT take the site's `alarmZones` list (from useAlarmConfigWithParents) —
// zone-id-to-mode matching is a static lookup (getAlarmMonitoringMode), so pulling in that async,
// separately-fetched list would make the armed state incorrectly depend on whether it has loaded
// yet (it defaults to [] until the fetch resolves), causing every camera to render as unarmed on
// first paint.
export const isCameraArmedForMode = (
  isSiteArmed: boolean,
  cameraTagIds: string[] | undefined,
  selectedMonitoringMode?: AlarmMonitoringMode,
): boolean => {
  if (!isSiteArmed) {
    return false;
  }
  if (!selectedMonitoringMode) {
    return true;
  }
  return cameraTagIds?.some((tagId) => isZoneActiveForMode(tagId, selectedMonitoringMode)) ?? false;
};

export const toggleItem = <T>(list: T[] | undefined, item: T, include: boolean) =>
  include ? union(list, [item]) : without(list, item);

export function userHasVerifEyeGroups(user: UserSession | undefined) {
  return !!user?.profile.verifEyeGroupIds?.length;
}
export const canShowVerifEyeConfigCard = ({
  canManageVerifEyeConfig,
  alarmProviderType,
  enableVerifeye4,
}: {
  canManageVerifEyeConfig: boolean;
  alarmProviderType: AlarmProviderType;
  enableVerifeye4: boolean;
}): boolean =>
  canManageVerifEyeConfig &&
  alarmProviderType === ALARM_PROVIDER_TYPES.MONITOR_CENTER &&
  enableVerifeye4;
